import { AGENT_TYPE_LABELS, formatAgentType } from './watchTowerSelectors';

import type {
  WatchCompany,
  WatchFinding,
  WatchRun,
  WatchSchedule,
  WatchScheduleTarget,
  WatchTargetKind,
  WatchTowerMockState,
} from './types';

export interface MockAgentType {
  type: string,
  subType: string,
  label: string,
}

export interface MockAgentTemplate {
  id: number,
  type: string,
  name: string,
  enabled: boolean,
}

export interface CreateWatchScheduleInput {
  companyIds: number[],
  type: string,
  subType: string,
  agentConfigIds: number[],
  timezone: string,
  hourOfDay: number,
  frequency: string,
  paused: boolean,
  targetsByCompany?: Record<number, WatchScheduleTarget[]>,
  now?: Date,
}

export type CreateWatchScheduleError =
  | 'no_companies'
  | 'no_targets'
  | 'no_template'
  | 'all_duplicates';

export type CreateWatchScheduleResult =
  | { ok: true, state: WatchTowerMockState, createdIds: number[], skippedCount: number }
  | { ok: false, error: CreateWatchScheduleError };

export const MONITORING_AGENT_TYPES: MockAgentType[] = [
  { type: 'regulation-monitoring', subType: 'regulation-tracking', label: AGENT_TYPE_LABELS['regulation-monitoring'] },
  { type: 'climate-monitoring', subType: 'climate-risk', label: AGENT_TYPE_LABELS['climate-monitoring'] },
  { type: 'reputation-monitoring', subType: 'media-sentiment', label: AGENT_TYPE_LABELS['reputation-monitoring'] },
  { type: 'finance-monitoring', subType: 'financial-risk', label: AGENT_TYPE_LABELS['finance-monitoring'] },
  { type: 'ownership-monitoring', subType: 'industrial-forecasting', label: AGENT_TYPE_LABELS['ownership-monitoring'] },
];

export const MOCK_TEMPLATES: MockAgentTemplate[] = [
  {
    id: 5, type: 'regulation-monitoring', name: 'Product Regulation default', enabled: true,
  },
  {
    id: 6, type: 'climate-monitoring', name: 'Climate default', enabled: true,
  },
  {
    id: 7, type: 'reputation-monitoring', name: 'Public Sentiment default', enabled: true,
  },
  {
    id: 4, type: 'finance-monitoring', name: 'Financial Stability default', enabled: true,
  },
  {
    id: 14, type: 'finance-monitoring', name: 'Financial Stability strict', enabled: true,
  },
  {
    id: 8, type: 'ownership-monitoring', name: 'Ownership default', enabled: true,
  },
  {
    id: 15, type: 'regulation-monitoring', name: 'Product Regulation EU pack', enabled: true,
  },
  {
    id: 99, type: 'regulation-monitoring', name: 'Disabled regulation', enabled: false,
  },
];

export const SCHEDULE_TIMEZONES = [
  'UTC',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Paris',
  'America/New_York',
];

export const SCHEDULE_FREQUENCIES = ['daily', 'weekly', 'monthly'] as const;

export const ALL_TARGET_KINDS: WatchTargetKind[] = ['company', 'product', 'building', 'org_node'];

const FINDING_TYPE_BY_AGENT: Record<string, string> = {
  'regulation-monitoring': 'regulation_signal',
  'climate-monitoring': 'climate_finding',
  'reputation-monitoring': 'media_article',
  'finance-monitoring': 'financial_finding',
  'ownership-monitoring': 'ownership_finding',
};

export function defaultTargetKinds(type: string): WatchTargetKind[] {
  if (type === 'regulation-monitoring') {
    return ['product'];
  }
  if (type === 'climate-monitoring') {
    return ['company', 'building'];
  }
  return ['company'];
}

export function enabledTemplatesForType(type: string): MockAgentTemplate[] {
  return MOCK_TEMPLATES.filter((item) => item.type === type && item.enabled);
}

export function agentTypeByCode(type: string): MockAgentType | undefined {
  return MONITORING_AGENT_TYPES.find((item) => item.type === type);
}

export function scheduleTargets(schedule: WatchSchedule): WatchScheduleTarget[] {
  if (schedule.targets && schedule.targets.length) {
    return schedule.targets;
  }
  return schedule.targetIds.map((id) => ({
    kind: schedule.targetKind as WatchTargetKind,
    id,
  }));
}

export function targetsAvailableForKind(
  company: WatchCompany,
  kind: WatchTargetKind,
): { id: number, label: string }[] {
  if (kind === 'company') {
    return [{ id: company.id, label: company.name }];
  }
  if (kind === 'product') {
    return company.products.map((item) => ({ id: item.id, label: item.name }));
  }
  if (kind === 'building') {
    return company.buildings.map((item) => ({ id: item.id, label: item.name }));
  }
  return company.orgNodes.map((item) => ({ id: item.id, label: item.name }));
}

export function targetKey(companyId: number, kind: WatchTargetKind, id: number): string {
  return `${companyId}:${kind}:${id}`;
}

export function parseTargetKey(key: string): {
  companyId: number,
  kind: WatchTargetKind,
  id: number,
} {
  const [companyId, kind, id] = key.split(':');
  return {
    companyId: Number(companyId),
    kind: kind as WatchTargetKind,
    id: Number(id),
  };
}

export function defaultTargetKeys(
  companies: WatchCompany[],
  type: string,
  extraKinds: WatchTargetKind[] = [],
): string[] {
  const kinds = Array.from(new Set([...defaultTargetKinds(type), ...extraKinds]));
  return companies.flatMap((company) => kinds.flatMap((kind) => (
    targetsAvailableForKind(company, kind).map((item) => targetKey(company.id, kind, item.id))
  )));
}

export function groupKeysByCompany(
  keys: string[],
  companyIds: number[],
): Record<number, WatchScheduleTarget[]> {
  return Object.fromEntries(
    companyIds.map((id) => [
      id,
      keys
        .map(parseTargetKey)
        .filter((item) => item.companyId === id)
        .map((item) => ({ kind: item.kind, id: item.id })),
    ]),
  );
}

export function nextScheduleId(state: WatchTowerMockState): number {
  return state.schedules.reduce((max, item) => Math.max(max, item.id), 0) + 1;
}

export function formatHourOfDay(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

export function mockNextRunAt(hourOfDay: number, frequency: string, from: Date): string {
  const next = new Date(from.getTime());
  if (frequency === 'weekly') {
    next.setUTCDate(next.getUTCDate() + 7);
  } else if (frequency === 'monthly') {
    next.setUTCMonth(next.getUTCMonth() + 1);
  } else {
    next.setUTCDate(next.getUTCDate() + 1);
  }
  next.setUTCHours(hourOfDay, 0, 0, 0);
  return next.toISOString();
}

export function mockPublicId(now = new Date()): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const stamp = String(now.getTime()).padStart(12, '0').slice(-12);
  return `00000000-0000-4000-8000-${stamp}`;
}

function buildScheduleTitle(
  companyName: string,
  templateName: string,
  frequency: string,
): string {
  return `${companyName} — ${templateName} ${frequency}`;
}

function primaryKindAndIds(targets: WatchScheduleTarget[]): {
  targetKind: WatchTargetKind,
  targetIds: number[],
} {
  const targetKind = targets[0]?.kind || 'company';
  return {
    targetKind,
    targetIds: targets.filter((item) => item.kind === targetKind).map((item) => item.id),
  };
}

export function hasAgentTemplateSchedule(
  schedules: WatchSchedule[],
  companyId: number,
  type: string,
  agentConfigId: number,
): boolean {
  return schedules.some((schedule) => (
    schedule.watchCompanyId === companyId
    && schedule.type === type
    && schedule.agentConfigId === agentConfigId
  ));
}

export function companyNeedsAgentTemplates(
  schedules: WatchSchedule[],
  companyId: number,
  type: string,
  agentConfigIds: number[],
): boolean {
  if (!agentConfigIds.length) {
    return true;
  }
  return agentConfigIds.some((id) => !hasAgentTemplateSchedule(schedules, companyId, type, id));
}

export interface DuplicateAgentTemplateWarning {
  companyId: number,
  companyName: string,
  templateId: number,
  templateName: string,
}

export function duplicateAgentTemplateWarnings(
  state: WatchTowerMockState,
  companyIds: number[],
  type: string,
  agentConfigIds: number[],
): DuplicateAgentTemplateWarning[] {
  const names = new Map(state.watchCompanies.map((company) => [company.id, company.name]));
  const templates = new Map(MOCK_TEMPLATES.map((item) => [item.id, item.name]));
  return companyIds.flatMap((companyId) => agentConfigIds.flatMap((templateId) => {
    if (!hasAgentTemplateSchedule(state.schedules, companyId, type, templateId)) {
      return [];
    }
    return [{
      companyId,
      companyName: names.get(companyId) || String(companyId),
      templateId,
      templateName: templates.get(templateId) || String(templateId),
    }];
  }));
}

function targetsForCompany(
  company: WatchCompany,
  type: string,
  provided?: WatchScheduleTarget[],
): WatchScheduleTarget[] {
  if (provided) {
    return provided;
  }
  return defaultTargetKeys([company], type)
    .map(parseTargetKey)
    .map((item) => ({ kind: item.kind, id: item.id }));
}

export function createWatchSchedules(
  state: WatchTowerMockState,
  input: CreateWatchScheduleInput,
): CreateWatchScheduleResult {
  const companies = input.companyIds
    .map((id) => state.watchCompanies.find((item) => item.id === id))
    .filter((item): item is WatchCompany => Boolean(item));
  if (!companies.length) {
    return { ok: false, error: 'no_companies' };
  }

  const templates = input.agentConfigIds
    .map((id) => MOCK_TEMPLATES.find((item) => (
      item.id === id && item.enabled && item.type === input.type
    )))
    .filter((item): item is MockAgentTemplate => Boolean(item));
  const uniqueTemplates = templates.filter((item, index) => (
    templates.findIndex((other) => other.id === item.id) === index
  ));
  if (!uniqueTemplates.length) {
    return { ok: false, error: 'no_template' };
  }

  const now = input.now || new Date();
  const created: WatchSchedule[] = [];
  let skippedCount = 0;
  let missingTargets = false;
  let nextId = nextScheduleId(state);

  companies.forEach((company) => {
    uniqueTemplates.forEach((template) => {
      if (hasAgentTemplateSchedule(state.schedules, company.id, input.type, template.id)
        || created.some((item) => (
          item.watchCompanyId === company.id && item.agentConfigId === template.id
        ))) {
        skippedCount += 1;
        return;
      }
      const targets = targetsForCompany(
        company,
        input.type,
        input.targetsByCompany?.[company.id],
      );
      if (!targets.length) {
        missingTargets = true;
        return;
      }
      const primary = primaryKindAndIds(targets);
      created.push({
        id: nextId,
        title: buildScheduleTitle(company.name, template.name, input.frequency),
        watchCompanyId: company.id,
        type: input.type,
        subType: input.subType,
        agentConfigId: template.id,
        templateName: template.name,
        timezone: input.timezone,
        hourOfDay: input.hourOfDay,
        frequency: input.frequency,
        paused: input.paused,
        nextRunAt: input.paused ? null : mockNextRunAt(input.hourOfDay, input.frequency, now),
        lastRunStatus: null,
        targetKind: primary.targetKind,
        targetIds: primary.targetIds,
        targets,
      });
      nextId += 1;
    });
  });

  if (missingTargets) {
    return { ok: false, error: 'no_targets' };
  }

  if (!created.length) {
    if (skippedCount) {
      return { ok: false, error: 'all_duplicates' };
    }
    return { ok: false, error: 'no_targets' };
  }

  return {
    ok: true,
    createdIds: created.map((item) => item.id),
    skippedCount,
    state: {
      ...state,
      schedules: [...state.schedules, ...created],
    },
  };
}

export function setSchedulePaused(
  state: WatchTowerMockState,
  scheduleId: number,
  paused: boolean,
  now = new Date(),
): WatchTowerMockState {
  return {
    ...state,
    schedules: state.schedules.map((schedule) => {
      if (schedule.id !== scheduleId) {
        return schedule;
      }
      return {
        ...schedule,
        paused,
        nextRunAt: paused ? null : mockNextRunAt(schedule.hourOfDay, schedule.frequency, now),
      };
    }),
  };
}

function nextRunId(state: WatchTowerMockState, extra: WatchRun[]): number {
  return [...state.runs, ...extra].reduce((max, item) => Math.max(max, item.id), 0) + 1;
}

function nextFindingId(state: WatchTowerMockState, extra: WatchFinding[]): number {
  return [...state.findings, ...extra].reduce((max, item) => Math.max(max, item.id), 0) + 1;
}

function demoFindingsForType(state: WatchTowerMockState, type: string): WatchFinding[] {
  const runIds = new Set(
    state.runs.filter((run) => run.type === type).map((run) => run.id),
  );
  return state.findings.filter((finding) => runIds.has(finding.agentRunId));
}

function summaryFromFindings(findings: WatchFinding[]): Record<string, number> {
  return findings.reduce<Record<string, number>>((counts, finding) => {
    if (finding.severity === 'high' || finding.severity === 'critical') {
      return { ...counts, high_risk_count: (counts.high_risk_count || 0) + 1 };
    }
    if (finding.severity === 'medium') {
      return { ...counts, medium_risk_count: (counts.medium_risk_count || 0) + 1 };
    }
    return { ...counts, low_risk_count: (counts.low_risk_count || 0) + 1 };
  }, { high_risk_count: 0, medium_risk_count: 0, low_risk_count: 0 });
}

export function runScheduleNow(
  state: WatchTowerMockState,
  scheduleId: number,
  now = new Date(),
): WatchTowerMockState {
  const schedule = state.schedules.find((item) => item.id === scheduleId);
  if (!schedule) {
    return state;
  }

  const targets = scheduleTargets(schedule);
  const stamp = now.toISOString();
  const demo = demoFindingsForType(state, schedule.type);
  const newRuns: WatchRun[] = [];
  const newFindings: WatchFinding[] = [];

  targets.forEach((target, index) => {
    const runId = nextRunId(state, newRuns);
    const source = demo[index] || demo[0];
    const finding: WatchFinding = source
      ? {
        ...source,
        id: nextFindingId(state, newFindings),
        publicId: mockPublicId(now),
        agentRunId: runId,
        watchCompanyId: schedule.watchCompanyId,
      }
      : {
        id: nextFindingId(state, newFindings),
        publicId: mockPublicId(now),
        agentRunId: runId,
        watchCompanyId: schedule.watchCompanyId,
        findingType: FINDING_TYPE_BY_AGENT[schedule.type] || 'finding',
        severity: 'high',
        title: `Mock ${formatAgentType(schedule.type)} finding`,
        description: 'Inserted by Run now in the Watch Tower mock.',
        observedAt: stamp,
        confidenceScore: 0.7,
        sourceUrl: null,
        payloadJson: { mock: true, targetKind: target.kind, targetId: target.id },
      };
    newRuns.push({
      id: runId,
      scheduleId: schedule.id,
      scheduledAt: stamp,
      watchCompanyId: schedule.watchCompanyId,
      watchTargetKind: target.kind,
      watchTargetId: target.id,
      type: schedule.type,
      status: 'completed',
      costUsd: 0.12,
      durationMs: 40000,
      summary: summaryFromFindings([finding]),
    });
    newFindings.push(finding);
  });

  return {
    ...state,
    runs: [...state.runs, ...newRuns],
    findings: [...state.findings, ...newFindings],
    schedules: state.schedules.map((item) => (
      item.id === scheduleId
        ? { ...item, lastRunStatus: 'completed' }
        : item
    )),
  };
}
